import { ArticleModel, ArticleModelArticleType, ArticleThemeModel, ArticleContributorModel, SectionModel, ArticleKeywordModel, ArticleImageModel, IssueModel, ArticleLocationModel } from "@/Generated/ParticaAPI";
import { Guid } from "../../BusinessServices/articleutils";


export interface ITOCArticle {
	id: number;
	issueId: number;
	articleId: string;
	sectionId: number;
	abstract: string;
	title: string;
	pageNumber: number;
	get<T>(): T;
}

export interface IMetadataArticle {
	id: number;
	issueId: number;
	sectionId: number;
	articleId: string;
	abstract: string;
	title: string;
	text: string;
	pageNumber: number;
	keywords: ArticleKeyword[];
	slug: string;
	addKeyword(keyword: string): ArticleKeyword;
	removeKeyword(item: ArticleKeyword): number;
	addContributor(contributor: string): ArticleContributor;
	// addLocation(location: string): ArticleLocation;
	get<T>(): T;
}

export interface ICuratedArticle {
	id: number;
	articleId: string;
	abstract: string;
	subheading: string;
	title: string;
	text: string;
	images: ArticleImage[];
	articleSocials: ArticleSocial;
	contributors: ArticleContributor[];
	addImage(image: ArticleImage): ArticleImage;
	removeImage(item: ArticleImage): number;
	get<T>(): T;
	getFeaturedImage(base: string, size: number): string;
	addContributor(contributor: string): ArticleContributor;
	removeContributor(contributor: ArticleContributor): number;
	getImageSrc(base: string, src: string, size: number): string;
}


export interface IWrittenArticle {
	id: number;
	articleId: string;
	sectionId: number;
	issueId: number;
	abstract: string;
	title: string;
	text: string;
	images: ArticleImage[];
	keywords: ArticleKeyword[];
	addImage(image: ArticleImage): ArticleImage;
	removeImage(item: ArticleImage): number;
	addKeyword(keyword: string): ArticleKeyword;
	removeKeyword(item: ArticleKeyword): number;
	setImageContributor(index: number, contributor: string): void;
	setImageCaption(index: number, caption: string): void;
	get<T>(): T;
	addTheme(theme: string): ArticleTheme;
	removeTheme(item: ArticleTheme): number;
}

export interface ISyndicatedArticle {
	id: number;
	articleId: string;
	sectionId: number;
	articleType: ArticleModelArticleType;
	issueId: number;
	abstract: string;
	title: string;
	text: string;
	images: ArticleImage[];
	keywords: ArticleKeyword[];
	addImage(image: ArticleImage): ArticleImage;
	removeImage(item: ArticleImage): number;
	addKeyword(keyword: string): ArticleKeyword;
	removeKeyword(item: ArticleKeyword): number;
	setImageContributor(index: number, contributor: string): void;
	setImageCaption(index: number, caption: string): void;
	get<T>(): T;
	addTheme(theme: string): ArticleTheme;
	removeTheme(item: ArticleTheme): number;
}




export interface IExtractedArticle {
	id: number;
	articleId: string;
	sectionId: number;
	issueId: number;
	abstract: string;
	title: string;
	text: string;
	images: ArticleImage[];
	keywords: ArticleKeyword[];
	addImage(image: ArticleImage): ArticleImage;
	removeImage(item: ArticleImage): number;
	addKeyword(keyword: string): ArticleKeyword;
	removeKeyword(item: ArticleKeyword): number;
	setImageContributor(index: number, contributor: string): void;
	setImageCaption(index: number, caption: string): void;
	get<T>(): T;
}

export interface IArticle {
	id: number;
	sectionId: number;
	issueId: number;
	parentArticleId?: string;
	articleType: ArticleModelArticleType;
	articleId?: string;
	articleAccess: number;
	slug: string;
	abstract: string;
	pageNumber: number;
	subheading: string;
	title: string;
	text: string;
	css: string;
	order: number;
	coordinates: string;
	hash: string;
	created: Date;
	modified: Date;
	articleDate: Date;
	price: number;
	themes: ArticleTheme[];
	contributors: ArticleContributor[];
	images: ArticleImage[];
	keywords: ArticleKeyword[];
	issue: ArticleIssue;
	articleSocials: ArticleSocial;
	selected: boolean;

	addContributor(contributor: string): ArticleContributor;
	removeContributor(contributor: ArticleContributor): number;
	addKeyword(keyword: string): ArticleKeyword;
	removeKeyword(item: ArticleKeyword): number;
	addTheme(theme: string): ArticleTheme;
	removeTheme(item: ArticleTheme): number;
	addImage(image: ArticleImage): ArticleImage;
	removeImage(item: ArticleImage): number;
	setImageContributor(index: number, contributor: string): void;
	setImageCaption(index: number, caption: string): void;
	get<T>(): T;
	getFeaturedImage(base: string, size: number): string;
	getImageSrc(base: string, src: string, size: number): string;
	// section: SectionModel;
	// issue: IssueModel;
}

// tslint:disable-next-line: max-classes-per-file
export class Article implements IArticle {
	public id: number = 0;
	public sectionId: number = 0;
	public issueId: number = 0;
	public articleId?: string;
	public title: string = "";

	public articleType: ArticleModelArticleType = ArticleModelArticleType.Extracted;
	public parentArticleId?: string;
	public created: Date = new Date();
	public modified: Date = new Date();
	public articleDate: Date = new Date();
	public articleAccess: number = 0;
	public slug: string = "";
	public abstract: string = "";
	public pageNumber: number = 0;
	public subheading: string = "";
	public text: string = "";
	public css: string = "";
	public order: number = 0;
	public coordinates: string = "";
	public hash: string = "";
	public price: number = 0;
	public images: ArticleImage[] = new Array<ArticleImage>();
	public keywords: ArticleKeyword[] = new Array<ArticleKeyword>();
	public themes: ArticleTheme[] = new Array<ArticleTheme>();
	public contributors: ArticleContributor[] = new Array<ArticleContributor>();
	public issue: ArticleIssue = new ArticleIssue();
	public articleSocials: ArticleSocial = new ArticleSocial();
	public locations: ArticleLocationModel[] = new Array<ArticleLocationModel>();
	public selected: boolean = false; // checkboxes

	public set(article: ArticleModel) {
		Object.assign(this, article);
		if (article.images !== undefined) { // change types so we can use functions.
			const tmpimages: ArticleImage[] = [];
			article.images.forEach(image => {
				const tmpimage: ArticleImage = new ArticleImage();
				Object.assign(tmpimage, image);
				tmpimages.push(tmpimage);
			});
			this.images = tmpimages;
		}

	}

	public get<T>(): T {
		const result: T = {} as T;
		Object.assign(result, this);
		return result;
	}

	public addContributor(contributor: string): ArticleContributor {
		const newContributor: ArticleContributor = new ArticleContributor();
		newContributor.contributor = contributor;
		newContributor.articleId = this.id;
		newContributor.id = 0;
		this.contributors.push(newContributor);
		return newContributor;
	}

	public removeContributor(item: ArticleContributor): number {
		const index = this.contributors.indexOf(item);
		if (index === -1) {
			return index;
		}
		this.contributors.splice(index, 1);
		return index;
	}


	public addLocation(locaion: string): ArticleLocation {
		/*const newLocation: ArticleLocationModel = new ArticleLocationModel();
		newLocation.l = contributor;
		newLocation.articleId = this.id;
		newLocation.id = 0;
		this.contributors.push(newLocation);
		return newLocation;*/
		return new ArticleLocation();
	}

	public removeLocation(item: ArticleLocationModel): number {
		const index = this.locations.indexOf(item);
		if (index === -1) {
			return index;
		}
		this.locations.splice(index, 1);
		return index;
	}


	public addTheme(theme: string): ArticleTheme {
		const newTheme: ArticleTheme = new ArticleTheme();
		newTheme.theme = theme;
		newTheme.articleId = this.id;
		newTheme.id = 0;
		this.themes.push(newTheme);
		return newTheme;
	}

	public removeTheme(item: ArticleTheme): number {
		const index = this.themes.indexOf(item);
		if (index === -1) {
			return index;
		}
		this.themes.splice(index, 1);
		return index;
	}


	public addKeyword(keyword: string): ArticleKeyword {
		const newKeyword: ArticleKeyword = new ArticleKeyword();
		newKeyword.keyword = keyword;
		newKeyword.articleId = this.id;
		newKeyword.id = 0;
		this.keywords.push(newKeyword);
		return newKeyword;
	}

	public removeKeyword(item: ArticleKeyword): number {
		const index = this.keywords.indexOf(item);
		if (index === -1) {
			return index;
		}
		this.keywords.splice(index, 1);
		return index;
	}

	public addImage(image: ArticleImage): ArticleImage {
		image.articleId = this.id;
		this.images.push(image);
		return image;
	}

	public removeImage(item: ArticleImage): number {
		const index = this.images.indexOf(item);
		if (index === -1) {
			return index;
		}
		this.images.splice(index, 1);
		return index;
	}

	public setImageCaption(index: number, caption: string): void {
		if (index < 0 || index > this.images.length) {
			throw new Error("Invalid image index.");
		}
		const image: ArticleImage = this.images[index];
		image.caption = caption;
	}

	public setImageContributor(index: number, contributor: string): void {
		if (index < 0 || index > this.images.length) {
			throw new Error("Invalid image index.");
		}
		const image: ArticleImage = this.images[index];
		image.contributor = contributor;
	}

	public getFeaturedImage(base: string, size: number): string {
		if (base === undefined) {
			throw new Error("Base path for image undefined.");
		}
		if (this.images && this.images.length > 0) {
			let img: string = "";
			if (this.images[0].src.startsWith("/")) {
				img = base + this.images[0].src.substring(1);
				if (size > 0) {
					img = img.replace(/.jpg/gi, `.${size}.jpg`);
				}
				return img;
			} else {
				if (this.issue === undefined) {
					return "https://dashboard.partica.online/app2/img/no-image.jpg";
				}
				img = base + this.issue.guid + "/embedimage/" + this.images[0].src;
				if (size > 0) {
					img = img.replace(/.jpg/gi, `.${size}.jpg`);
				}
				return img;
			}
		}
		return "https://dashboard.partica.online/app2/img/no-image.jpg";
	}

	public getImageSrc(base: string, src: string, size: number): string {
		if (base === undefined) {
			throw new Error("Base path for image undefined.");
		}
		if (this.images && this.images.length > 0) {
			let img: string = "";
			const found: ArticleImage | undefined = this.images.find(i => i.src === src);
			if (found === undefined) {
				return "https://dashboard.partica.online/app2/img/no-image.jpg";
			}
			if (found.src.startsWith("/")) {
				img = base + found.src.substring(1);
				if (size > 0) {
					img = img.replace(/.jpg/gi, `.${size}.jpg`);
				}
				return img;
			} else {
				if (this.issue === undefined) {
					console.log("No issue guid for image.");
					return "https://dashboard.partica.online/app2/img/no-image.jpg";
				}
				img = base + this.issue.guid + "/embedimage/" + found.src;
				if (size > 0) {
					img = img.replace(/.jpg/gi, `.${size}.jpg`);
				}
				return img;
			}
		}
		return "https://dashboard.partica.online/app2/img/no-image.jpg";
	}



}
// tslint:disable-next-line: max-classes-per-file
export class ArticleKeyword {
	public id: number;
	public articleId: number;
	public keyword: string;
}

// tslint:disable-next-line: max-classes-per-file
export class ArticleLocation {
	public id: number;
	public articleId: number;
	public locationId: number;
}


// tslint:disable-next-line: max-classes-per-file
export class ArticleSocial {
	public articleSocialId: number = 0;
	public linkedin: string = "";
	public facebook: string = "";
	public instagram: string = "";
}


// tslint:disable-next-line: max-classes-per-file
export class ArticleImage {
	public id: number = 0;
	public articleId: number = 0;
	public aiType: string = "";
	public backgroundPosition: string = "";
	public caption: string = "";
	public contributor: string = "";
	public isFeatured: boolean = false;
	public isFirst: boolean = false;
	public src: string = "";
	public uid: string = "";

	public getPath(base: string, issue: string): string {
		if (this.src.startsWith("/")) {
			return base + this.src.substring(1);
		} else {
			return base + issue + "/embedimage/" + this.src;
		}
		return "";
	}
}

// tslint:disable-next-line: max-classes-per-file
export class ArticleTheme {
	public id: number = 0;
	public articleId: number = 0;
	public theme: string = "";
}
// tslint:disable-next-line: max-classes-per-file
export class ArticleContributor {
	public id: number = 0;
	public articleId: number = 0;
	public contributor: string = "";
}

// tslint:disable-next-line: max-classes-per-file
export class ArticleIssue {
	public id: number = 0;
	public guid: string = "";
	public name: string = "";
	public pubname: string = "";
}


export enum ArticleCountry {
    Afghanistan = "Afghanistan",
    Albania = "Albania",
    Algeria = "Algeria",
    Andorra = "Andorra",
    Angola = "Angola",
    Antarctica = "Antarctica",
    Argentina = "Argentina",
    Armenia = "Armenia",
    Aruba = "Aruba",
    Australia = "Australia",
    Austria = "Austria",
    Azerbaijan = "Azerbaijan",
    Bahrain = "Bahrain",
    Bangladesh = "Bangladesh",
    Belarus = "Belarus",
    Belgium = "Belgium",
    Belize = "Belize",
    Benin = "Benin",
    Bhutan = "Bhutan",
    Bolivia = "Bolivia",
    BosniaHerzegovina = "Bosnia Herzegovina",
    Botswana = "Botswana",
    Brazil = "Brazil",
    BruneiDarussalam = "Brunei Darussalam",
    Bulgaria = "Bulgaria",
    BurkinaFaso = "Burkina Faso",
    Myanmar = "Myanmar",
    Burundi = "Burundi",
    Cambodia = "Cambodia",
    Cameroon = "Cameroon",
    Canada = "Canada",
    CapeVerde = "Cape Verde",
    CentralAfricanRepublic = "Central African Republic",
    Chad = "Chad",
    Chile = "Chile",
    China = "China",
    ChristmasIsland = "Christmas Island",
    CocosIslands = "Cocos Islands",
    Colombia = "Colombia",
    Comoros = "Comoros",
    Congo = "Congo",
    DemocraticRepublicCongo = "Democratic Republic Congo",
    CookIslands = "Cook Islands",
    CostaRica = "Costa Rica",
    Croatia = "Croatia",
    Cuba = "Cuba",
    Cyprus = "Cyprus",
    CzechRepublic = "Czech Republic",
    Denmark = "Denmark",
    Djibouti = "Djibouti",
    Timorleste = "Timor-leste",
    Ecuador = "Ecuador",
    Egypt = "Egypt",
    ElSalvador = "El Salvador",
    EquatorialGuinea = "Equatorial Guinea",
    Eritrea = "Eritrea",
    Estonia = "Estonia",
    Ethiopia = "Ethiopia",
    FalklandIslands = "Falkland Islands",
    FaroeIslands = "Faroe Islands",
    Fiji = "Fiji",
    Finland = "Finland",
    France = "France",
    FrenchPolynesia = "French Polynesia",
    Gabon = "Gabon",
    Gambia = "Gambia",
    Georgia = "Georgia",
    Germany = "Germany",
    Ghana = "Ghana",
    Gibraltar = "Gibraltar",
    Greece = "Greece",
    Greenland = "Greenland",
    Guatemala = "Guatemala",
    Guinea = "Guinea",
    Guineabissau = "Guinea-Bissau",
    Guyana = "Guyana",
    Haiti = "Haiti",
    Honduras = "Honduras",
    HongKong = "Hong Kong",
    Hungary = "Hungary",
    India = "India",
    Indonesia = "Indonesia",
    Iran = "Iran",
    Iraq = "Iraq",
    Ireland = "Ireland",
    IsleOfMan = "Isle Of Man",
    Israel = "Israel",
    Italy = "Italy",
    CoteDivoire = "Cote D'ivoire",
    Japan = "Japan",
    Jordan = "Jordan",
    Kazakhstan = "Kazakhstan",
    Kenya = "Kenya",
    Kiribati = "Kiribati",
    Kuwait = "Kuwait",
    Kyrgyzstan = "Kyrgyzstan",
    LaoPeoplesDR = "Lao People's Democratic Republic",
    Latvia = "Latvia",
    Lebanon = "Lebanon",
    Lesotho = "Lesotho",
    Liberia = "Liberia",
    Libya = "Libya",
    Liechtenstein = "Liechtenstein",
    Lithuania = "Lithuania",
    Luxembourg = "Luxembourg",
    Macao = "Macao",
    Macedonia = "Macedonia",
    Madagascar = "Madagascar",
    Malawi = "Malawi",
    Malaysia = "Malaysia",
    Maldives = "Maldives",
    Mali = "Mali",
    Malta = "Malta",
    Mauritania = "Mauritania",
    Mauritius = "Mauritius",
    Mayotte = "Mayotte",
    Mexico = "Mexico",
    Micronesia = "Micronesia",
    Moldova = "Moldova",
    Monaco = "Monaco",
    Mongolia = "Mongolia",
    Montenegro = "Montenegro",
    Morocco = "Morocco",
    Mozambique = "Mozambique",
    Namibia = "Namibia",
    Nauru = "Nauru",
    Nepal = "Nepal",
    Netherlands = "Netherlands",
    NewCaledonia = "New Caledonia",
    NewZealand = "New Zealand",
    Nicaragua = "Nicaragua",
    Niger = "Niger",
    Nigeria = "Nigeria",
    Niue = "Niue",
    NorthKorea = "North Korea",
    Norway = "Norway",
    Oman = "Oman",
    Pakistan = "Pakistan",
    Palau = "Palau",
    Panama = "Panama",
    PapuaNewGuinea = "Papua New Guinea",
    Paraguay = "Paraguay",
    Peru = "Peru",
    Philippines = "Philippines",
    Poland = "Poland",
    Portugal = "Portugal",
    PuertoRico = "Puerto Rico",
    Qatar = "Qatar",
    Romania = "Romania",
    RussianFederation = "Russian Federation",
    Rwanda = "Rwanda",
    SaintBarthélemy = "Saint Barthélemy",
    Samoa = "Samoa",
    SanMarino = "SanMarino",
    SaoTomeAndPrincipe = "Sao Tome And Principe",
    SaudiArabia = "Saudi Arabia",
    Senegal = "Senegal",
    Serbia = "Serbia",
    Seychelles = "Seychelles",
    SierraLeone = "Sierra Leone",
    Singapore = "Singapore",
    Slovakia = "Slovakia",
    Slovenia = "Slovenia",
    SolomonIslands = "Solomon Islands",
    Somalia = "Somalia",
    SouthAfrica = "South Africa",
    SouthKorea = "South Korea",
    Spain = "Spain",
    SriLanka = "Sri Lanka",
    SaintHelena = "Saint Helena",
    SaintPierreMiquelon = "Saint Pierre Miquelon",
    Sudan = "Sudan",
    Suriname = "Suriname",
    Swaziland = "Swaziland",
    Sweden = "Sweden",
    Switzerland = "Switzerland",
    SyrianArabRepublic = "Syrian Arab Republic",
    Taiwan = "Taiwan",
    Tajikistan = "Tajikistan",
    Tanzania = "Tanzania",
    Thailand = "Thailand",
    Togo = "Togo",
    Tokelau = "Tokelau",
    Tonga = "Tonga",
    Tunisia = "Tunisia",
    Turkey = "Turkey",
    Turkmenistan = "Turkmenistan",
    Tuvalu = "Tuvalu",
    UnitedArabEmirates = "United Arab Emirates",
    Uganda = "Uganda",
    UnitedKingdom = "United Kingdom",
    Ukraine = "Ukraine",
    Uruguay = "Uruguay",
    UnitedStates = "United States",
    Uzbekistan = "Uzbekistan",
    Vanuatu = "Vanuatu",
    VaticanCity = "vatican City",
    HolySee = "Holy See",
    Venezuela = "Venezuela",
    VietNam = "Vietnam",
    WallisFutuna = "Wallis Futuna",
    Yemen = "Yemen",
    Zambia = "Zambia",
    Zimbabwe = "Zimbabwe",
    MarshallIslands = "Marshall Islands",
    Pitcairn = "Pitcairn",
}